import {
  getDefault,
  constants,
} from '@pearsontechnology/aero-plugin-api';

import ActivityService from 'services/ActivityService';
import PluginService from 'services/PluginService';

import * as coursewareTypes from 'pluginServices/constants/coursewareTypes';
import invokePlugin from './lifecycle/invokePlugin';
import { removePluginInstance } from './lifecycle';
import * as lifecycleInteractive from './lifecycle/interactive';
import { setStore } from './store';
import * as dispatcher from './event/dispatch';

import AnnotationServices from './services/AnnotationServices';
import AssetServices from './services/AssetServices';
import CoursewareItemServices from './services/CoursewareItemServices';
import CoursewareTargetService from './services/CoursewareTargetService';
import DeprecatedResourceService from './services/DeprecatedResourceService';
import DragStateServices from './services/DragStateServices';
import EditorServices from './services/EditorServices';
import FeatureFlagService from './services/FeatureFlagService';
import LifecycleServices from './services/LifecycleServices';
import LTIService from './services/LTIServices';
import PluginServices from './services/PluginServices';
import RouteService from './services/RouteServices';
import SelectionService from './services/SelectionServices';
import ThemeServices from './services/ThemeServices';
import UserServices from './services/UserServices';
import EventService from './services/EventService';
import HistoryService from './services/HistoryService';
import LTService from './services/LTService';

/**
 * Get the "default" initialization of the plugin library, rather
 * than initializing our own.
 */
const {
  services,
  CDP,
} = getDefault();

/**
 * Initializes the plugin services required by the plugin library.  This
 *   is running on an older plugin API where the services registry(`services`)
 *   is managed by the plugin library, effectively making it a singleton.  Newer
 *   users of the Plugin Library may instantiate the service registry manually,
 *   thus enabling multiple root plugin instances to run concurrently.
 *
 * See the plugin library for more information, but the general architecture is
 *   for a plugin to communicate with an application (this), it needs to pass through
 *   the following things (plugin -> resource -> resourceProvider -> Plugin services).
 */
export const registerPluginServices = () => {
  services
    .add(constants.ANNOTATIONS_SERVICE, AnnotationServices)
    .add(constants.ASSET_SERVICE, AssetServices)
    .add(constants.COURSEWARE_ITEM_SERVICE, CoursewareItemServices)
    .add(constants.COURSEWARE_TARGET_SERVICE, CoursewareTargetService)
    .add(constants.DEPRECATED_RESOURCE_SERVICE, DeprecatedResourceService)
    .add(constants.DRAG_STATE_SERVICE, DragStateServices)
    .add(constants.EDITOR_SERVICE, EditorServices)
    .add(constants.FEATURE_FLAG_SERVICE, FeatureFlagService)
    .add(constants.LIFECYCLE_SERVICE, LifecycleServices)
    .add(constants.LTI_SERVICE, LTIService)
    .add(constants.PLUGIN_SERVICE, PluginServices)
    .add(constants.ROUTE_SERVICE, RouteService)
    .add(constants.SELECTION_SERVICE, SelectionService)
    .add(constants.THEME_SERVICE, ThemeServices)
    .add(constants.USER_SERVICE, UserServices)
    .add(constants.EVENT_SERVICE, EventService)
    .add(constants.HISTORY_SERVICE, HistoryService)
    .add(constants.LT_SERVICE, LTService);
};

export const init = (store, sagaMiddleware) => {
  dispatcher.initialize(sagaMiddleware);
  setStore(store);

  registerPluginServices();
};

/**
 * Initialize a plugin view for an activity
 * @param {string} instanceId The courseware Id.
 * @param {*} manifest The plugin manifest (see dev docs on the plugin manifest)
 * @param {DomElement} element The element in which to attach the render
 * @param {string} context The context in which to render the plugin.  E.g.,
 *   EDITOR, VIEWER, etc...
 */
export const initializeActivityView = async (instanceId, manifest, element, context) => {
  const activity = await ActivityService.getActivity(instanceId);
  const { pluginId, version } = activity.plugin;

  const plugin = await PluginService.getPlugin(pluginId, version /* , context */);
  invokePlugin(
    instanceId,
    coursewareTypes.ACTIVITY,
    manifest || plugin.manifest,
    element,
    context
  );
};

/**
 * Destroy an existing activity plugin.
 * @param {string} instanceId
 */
export const removeActivityView = (instanceId) => {
  removePluginInstance(instanceId);
};

/**
 * Initialize a plugin view for an interactive.
 * @param {string} instanceId The courseware Id.
 * @param {*} manifest The plugin manifest (see dev docs on the plugin manifest)
 * @param {DomElement} element The element in which to attach the render
 * @param {string} context The context in which to render the plugin.  E.g.,
 *   EDITOR, VIEWER, etc...
 */
export const initializeInteractiveView = (interactiveId, manifest, element, context) => {
  lifecycleInteractive.initializeInteractiveView(
    interactiveId,
    manifest,
    element,
    context
  );
};

/**
 * Destroy an existing interactive plugin.
 * @param {string} instanceId
 */
export const removeInteractiveView = (interactiveId) => {
  lifecycleInteractive.removeInteractiveView(interactiveId);
};

export const initializeRoute = (target) => RouteService.build(target);

export { CDP };
